import { Table } from 'antd';
import { PureComponent, ReactNode } from 'react';
import { columnHeaderContent, headerStyle } from '../common/TableUtils';
import { Item } from '../../api/fetchPastVitalsByPtOid';
import { AntTooltip } from '../common/AntTooltip';
import { getColorClass } from '../common/MediaViewer';
import { getDayWord } from '../../pages/utils';
import { track } from '../../tracking';
import { InterpretationTooltip, RangeTooltip } from './MedicalRecordsComponent';
import { MISC_KEYS, getItemFromMisc } from '../../store/idbHandler';
import { getMyDbId } from '../../api/mactiveid';
import { sortKeyvitalsPastVisits } from './HistoricalLabVitalsContent';
import { AnimatePresence } from 'framer-motion';
import VitalTrendPopUp from '../../pages/VitalTrendPopup';

const { Column } = Table;

type Props = {
    fData: Item[];
    columnCount: number;
    customColumnWidth?: number[];
    keyVitals?: string[];
    toggleKeyVital?: (vitalId: string) => Promise<void>;
    patientId: string;
};

type State = {
    selectedVitalId?: string;
    openTrendPopUp?: boolean;
    keyVitals?: string[];
};

const defaultColumnWidth = [300, 250];

class LabVitalsHistoricalTable extends PureComponent<Props, State> {
    state: State = {};

    componentDidMount = async (): Promise<void> => {
        getItemFromMisc<string[]>(getMyDbId() || '', MISC_KEYS.keyVitals)
            .then((keyVitals) => {
                this.setState({ keyVitals });
            })
            .catch((e) => console.error(e));
    };

    testNameHeaderContent = columnHeaderContent('Test Name');
    latestReadingHeaderContent = columnHeaderContent('Latest reading');
    previousReadingsHeaderContent = columnHeaderContent('Previous readings');
    viewTrendHeaderContent = columnHeaderContent('View trend');

    renderTestName = (_: any, el: Item, index: number) => {
        return {
            children: (
                <div className="flex space-x-8 items-center">
                    <span>
                        {this.props.keyVitals &&
                            (this.props.keyVitals.length !== 0 &&
                            el.id &&
                            this.props.keyVitals.includes(el.id) ? (
                                <AntTooltip placement="right" content="Key vital">
                                    <svg
                                        viewBox="0 0 14 14"
                                        xmlns="http://www.w3.org/2000/svg"
                                        onClick={() =>
                                            this.props.toggleKeyVital &&
                                            this.props.toggleKeyVital(el.id as string)
                                        }
                                        className="w-15 h-14 cursor-pointer text-13"
                                    >
                                        <path
                                            d="M7.71094 1.08203L9.36133 4.45898L12.9922 4.99219C13.2969 5.04297 13.5508 5.24609 13.6523 5.55078C13.7539 5.83008 13.6777 6.16016 13.4492 6.36328L10.8086 8.97852L11.4434 12.6855C11.4941 12.9902 11.3672 13.2949 11.1133 13.4727C10.8594 13.6758 10.5293 13.6758 10.25 13.5488L7 11.7969L3.72461 13.5488C3.4707 13.6758 3.14062 13.6758 2.88672 13.4727C2.63281 13.2949 2.50586 12.9902 2.55664 12.6855L3.16602 8.97852L0.525391 6.36328C0.322266 6.16016 0.246094 5.83008 0.322266 5.55078C0.423828 5.24609 0.677734 5.04297 0.982422 4.99219L4.63867 4.45898L6.26367 1.08203C6.39062 0.802734 6.66992 0.625 7 0.625C7.30469 0.625 7.58398 0.802734 7.71094 1.08203Z"
                                            fill="#EDAE0C"
                                        />
                                    </svg>
                                </AntTooltip>
                            ) : (
                                <AntTooltip placement="right" content="Add as key vital">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 576 512"
                                        onClick={() =>
                                            this.props.toggleKeyVital &&
                                            this.props.toggleKeyVital(el.id as string)
                                        }
                                        className="w-15 h-14 cursor-pointer text-13"
                                    >
                                        <path d="M287.9 0c9.2 0 17.6 5.2 21.6 13.5l68.6 141.3 153.2 22.6c9 1.3 16.5 7.6 19.3 16.3s.5 18.1-5.9 24.5L433.6 328.4l26.2 155.6c1.5 9-2.2 18.1-9.6 23.5s-17.3 6-25.3 1.7l-137-73.2L151 509.1c-8.1 4.3-17.9 3.7-25.3-1.7s-11.2-14.5-9.7-23.5l26.2-155.6L31.1 218.2c-6.5-6.4-8.7-15.9-5.9-24.5s10.3-14.9 19.3-16.3l153.2-22.6L266.3 13.5C270.4 5.2 278.7 0 287.9 0zm0 79L235.4 187.2c-3.5 7.1-10.2 12.1-18.1 13.3L99 217.9 184.9 303c5.5 5.5 8.1 13.3 6.8 21L171.4 443.7l105.2-56.2c7.1-3.8 15.6-3.8 22.6 0l105.2 56.2L384.2 324.1c-1.3-7.7 1.2-15.5 6.8-21l85.9-85.1L358.6 200.5c-7.8-1.2-14.6-6.1-18.1-13.3L287.9 79z" />
                                    </svg>
                                </AntTooltip>
                            ))}
                    </span>
                    <span className="break-normal font-700 text-14">{el.name}</span>
                </div>
            ),
        };
    };

    renderValueAtIndex = (valueIndex: number) => (_: any, el: Item) => {
        const value = (el.values || [])[valueIndex];
        const { result } = value || {};

        return {
            children: value ? (
                <div
                    className={`flex space-x-8 items-center ${getColorClass(
                        value?.interp?.v,
                        'text-neutral-black fill-darwin-neutral-900',
                        'text-neutral-black fill-darwin-neutral-900',
                    )}`}
                    id={`${el.id}-${value.document_id}-${valueIndex}`}
                >
                    {value.actor === 'Patient' ? (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                            className="min-w-12 h-12"
                        >
                            <path d="M224 256c70.7 0 128-57.31 128-128s-57.3-128-128-128C153.3 0 96 57.31 96 128S153.3 256 224 256zM274.7 304H173.3C77.61 304 0 381.6 0 477.3c0 19.14 15.52 34.67 34.66 34.67h378.7C432.5 512 448 496.5 448 477.3C448 381.6 370.4 304 274.7 304z" />
                        </svg>
                    ) : (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 384 512"
                            className="min-w-12 h-12"
                        >
                            <path d="M256 0v128h128L256 0zM224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM272 416h-160C103.2 416 96 408.8 96 400C96 391.2 103.2 384 112 384h160c8.836 0 16 7.162 16 16C288 408.8 280.8 416 272 416zM272 352h-160C103.2 352 96 344.8 96 336C96 327.2 103.2 320 112 320h160c8.836 0 16 7.162 16 16C288 344.8 280.8 352 272 352zM288 272C288 280.8 280.8 288 272 288h-160C103.2 288 96 280.8 96 272C96 263.2 103.2 256 112 256h160C280.8 256 288 263.2 288 272z" />
                        </svg>
                    )}
                    <RangeTooltip
                        eleId={`${el.id}-${value.document_id}-${valueIndex}`}
                        vital={value}
                    >
                        <div className="flex flex-wrap cursor-pointer">
                            <span className={`body-2 mr-8 ${getColorClass(result, '', '')}`}>
                                {value.vl}
                            </span>
                            <span className="text-13 text-darwin-neutral-600">
                                {getDayWord(value.isoDateObject, "DD MMM'YY")}
                            </span>
                        </div>
                    </RangeTooltip>
                    <InterpretationTooltip
                        eleId={`${el.id}-${value.document_id}-${valueIndex}`}
                        vital={value}
                    />
                </div>
            ) : (
                <svg
                    width="8"
                    height="7"
                    viewBox="0 0 8 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M7.05469 5.32031C7.30859 5.55469 7.30859 5.96484 7.05469 6.19922C6.9375 6.31641 6.78125 6.375 6.625 6.375C6.44922 6.375 6.29297 6.31641 6.17578 6.19922L4.125 4.14844L2.05469 6.19922C1.9375 6.31641 1.78125 6.375 1.625 6.375C1.44922 6.375 1.29297 6.31641 1.17578 6.19922C0.921875 5.96484 0.921875 5.55469 1.17578 5.32031L3.22656 3.25L1.17578 1.19922C0.921875 0.964844 0.921875 0.554688 1.17578 0.320312C1.41016 0.0664062 1.82031 0.0664062 2.05469 0.320312L4.125 2.37109L6.17578 0.320312C6.41016 0.0664062 6.82031 0.0664062 7.05469 0.320312C7.30859 0.554688 7.30859 0.964844 7.05469 1.19922L5.00391 3.26953L7.05469 5.32031Z"
                        fill="#3E445C"
                    />
                </svg>
            ),
        };
    };

    renderViewTrend = (_: any, el: Item) => {
        return {
            children: (
                <div
                    className="flex items-center justify-end pr-4 cursor-pointer"
                    onClick={() => {
                        track('OPEN_VITAL_GRAPH_RIGHT_POPUP', { vitalId: el.id });
                        this.setSelectedVitalAndOpenTrendPopUp(el.id);
                    }}
                >
                    <svg
                        className="w-8 h-14 fill-darwin-accent-bright-blue-800"
                        viewBox="0 0 8 14"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M7.53125 6.46875C7.8125 6.78125 7.8125 7.25 7.53125 7.53125L1.53125 13.5312C1.21875 13.8438 0.75 13.8438 0.46875 13.5312C0.15625 13.25 0.15625 12.7812 0.46875 12.5L5.9375 7.03125L0.46875 1.53125C0.15625 1.25 0.15625 0.78125 0.46875 0.5C0.75 0.1875 1.21875 0.1875 1.5 0.5L7.53125 6.46875Z" />
                    </svg>
                </div>
            ),
        };
    };

    setSelectedVitalAndOpenTrendPopUp = (selectedVitalId: string) => {
        this.setState({
            selectedVitalId,
            openTrendPopUp: true,
        });
    };

    closePopUp = () => {
        this.setState({ selectedVitalId: undefined, openTrendPopUp: false });
    };

    render(): ReactNode {
        const { columnCount, customColumnWidth, fData, patientId } = this.props;
        const { selectedVitalId, openTrendPopUp } = this.state;

        const sortedFData =
            this.state.keyVitals && this.state.keyVitals.length !== 0
                ? sortKeyvitalsPastVisits(fData, this.state.keyVitals)
                : fData;

        return (
            <div className="overflow-hidden rounded-8 flex h-full flex-col">
                {!fData || fData.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center  space-y-16 select-none">
                        No data found
                    </div>
                ) : (
                    <div className="overflow-hidden flex flex-col h-full">
                        <Table
                            className={'h-full overflow-auto flex'}
                            rowClassName={(_, index) =>
                                `editable-row pt-last-vital-${fData[index].id} pt-last-vital-row`
                            }
                            bordered
                            rowKey={(record, index) => `${record.id}-${index}`}
                            dataSource={fData}
                            pagination={false}
                            sticky
                            scroll={{ x: '50%', y: 'calc(100% - 36px)' }}
                        >
                            <Column
                                title={this.testNameHeaderContent}
                                onHeaderCell={headerStyle}
                                key="past-vital-row-name"
                                className="lab-vital-row-name"
                                render={this.renderTestName}
                                fixed
                                width={customColumnWidth?.[0] || defaultColumnWidth[0]}
                            />

                            {new Array(columnCount).fill(0).map((item, index) => (
                                <Column
                                    title={
                                        index === 0
                                            ? this.latestReadingHeaderContent
                                            : index === 1
                                            ? this.previousReadingsHeaderContent
                                            : undefined
                                    }
                                    onHeaderCell={() => ({
                                        style: {
                                            backgroundColor:
                                                'rgba(244, 246, 249, var(--tw-bg-opacity))',
                                            padding: '8px 12px',
                                        },
                                    })}
                                    key={`past-vital-row-value-${index}`}
                                    className="lab-vital-row-name"
                                    render={this.renderValueAtIndex(index)}
                                    width={customColumnWidth?.[1] || defaultColumnWidth[1]}
                                />
                            ))}
                            {patientId && (
                                <Column
                                    title={this.viewTrendHeaderContent}
                                    onHeaderCell={headerStyle}
                                    key="past-vital-row-view-trend"
                                    className="lab-vital-row-view-trend"
                                    render={this.renderViewTrend}
                                    fixed="right"
                                    width={100}
                                />
                            )}
                        </Table>
                    </div>
                )}

                <AnimatePresence>
                    {selectedVitalId && openTrendPopUp && patientId && (
                        <VitalTrendPopUp
                            fData={sortedFData}
                            selectedVitalId={selectedVitalId}
                            closePopUp={this.closePopUp}
                            patientId={patientId}
                        />
                    )}
                </AnimatePresence>
            </div>
        );
    }
}

export default LabVitalsHistoricalTable;
